import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import useKeyDownListener from 'hooks/useKeyDownListener';
import Checkbox from 'layout/atoms/Checkbox/Checkbox';
import Input from 'layout/atoms/Input/Input';
import Label from 'layout/atoms/Label';
import Modal from 'layout/atoms/Modal/Modal';
import FormField from 'layout/molecules/FormField/FormField';
import FormSaveActions from 'layout/molecules/FormSaveActions/FormSaveActions';
import Grid from 'layout/molecules/Grid/Grid';
import { closeModal, selectModalByName } from 'store/layoutSlice';
import { selectProductCategoriesStatus, selectProductCategoryById, updateProductCategory, } from 'store/parameters/productCategoriesSlice';
import useProductCategoryValidation from './useProductCategoryValidation';
function ProductCategoryEditor() {
    var _a;
    const dispatch = useDispatch();
    const { t } = useTranslation(['specification', 'common']);
    const productCategoryId = (_a = useSelector(selectModalByName('productCategoryEditor'))) === null || _a === void 0 ? void 0 : _a.id;
    const productCategory = useSelector((state) => selectProductCategoryById(state, productCategoryId || '')) ||
        null;
    const status = useSelector(selectProductCategoriesStatus);
    const [formValues, setFormValues] = useState({
        typeLabelPL: '',
        typeLabelEN: '',
        filtersAvailable: false,
    });
    const { errors, isValid, pendingFields, markTouched, validateAll } = useProductCategoryValidation(formValues, t);
    useEffect(() => {
        if (productCategory) {
            setFormValues({
                typeLabelPL: productCategory.typeLabelPL || '',
                typeLabelEN: productCategory.typeLabelEN || '',
                filtersAvailable: Boolean(productCategory.filtersAvailable),
            });
        }
    }, [productCategory]);
    const handleFormChange = ({ target: { name, value } }) => {
        setFormValues((values) => (Object.assign(Object.assign({}, values), { [name]: value })));
    };
    const handleCancel = () => {
        dispatch(closeModal({ name: 'productCategoryEditor' }));
    };
    const handleFiltersAvailableToggle = () => {
        setFormValues((values) => (Object.assign(Object.assign({}, values), { filtersAvailable: !values.filtersAvailable })));
    };
    const handleConfirm = () => {
        if (!validateAll())
            return;
        if (!productCategory)
            return;
        dispatch(updateProductCategory({
            id: productCategory.id,
            typeLabelPL: formValues.typeLabelPL.trim(),
            typeLabelEN: formValues.typeLabelEN.trim(),
            filtersAvailable: formValues.filtersAvailable,
        }));
        dispatch(closeModal({ name: 'productCategoryEditor' }));
    };
    const isSaving = status.update === 'loading';
    useKeyDownListener([
        { code: 'Enter', callback: handleConfirm },
        { code: 'Escape', callback: handleCancel },
    ]);
    return (React.createElement(Modal, { name: "productCategoryEditor", size: "s", header: React.createElement(Label, { weight: "strong" }, t('edit_product_category')), footer: React.createElement(FormSaveActions, { isValid: isValid, isLoading: isSaving, pendingFields: pendingFields, onSave: handleConfirm, onCancel: handleCancel, validateAll: validateAll }), onClose: handleCancel },
        React.createElement(Grid, null,
            React.createElement(FormField, { label: t('product_category_name_pl'), error: errors.typeLabelPL },
                React.createElement(Input, { value: formValues.typeLabelPL, onChange: handleFormChange, onBlur: () => markTouched('typeLabelPL'), name: "typeLabelPL", error: Boolean(errors.typeLabelPL), autoFocus: true, required: true })),
            React.createElement(FormField, { label: t('product_category_name_en'), error: errors.typeLabelEN },
                React.createElement(Input, { value: formValues.typeLabelEN, onChange: handleFormChange, onBlur: () => markTouched('typeLabelEN'), name: "typeLabelEN", error: Boolean(errors.typeLabelEN), required: true })),
            React.createElement(Checkbox, { size: "m", label: t('product_category_filters_available'), value: formValues.filtersAvailable, onClick: handleFiltersAvailableToggle }))));
}
export default memo(ProductCategoryEditor);
